import {
  type CSSProperties,
  memo,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

import type {
  BookLineInput,
  BookLinksInput,
  SettlementTreatmentEnum,
  TagColorEnum,
  TransactionFilter,
  TriageListItemFragment,
} from "@/__generated__/graphql";
import {
  BookingPartEnum,
  TriageListItemFragmentDoc,
} from "@/__generated__/graphql";
import { useApolloClient } from "@apollo/client/react";
import {
  ArrowLeftRightIcon,
  CalendarIcon,
  CircleCheckIcon,
  type LucideIcon,
} from "lucide-react";

import type { FilterType } from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { SelectableCardList } from "@/components/molecules/SelectableCardList";
import { icons, iconsByTypename } from "@/objects/@icons";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { State } from "@/state";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/components/ui/utils";

import {
  TRIAGE_LINK_BALL_FADE_MS,
  TRIAGE_RECONCILE_ROW_PLACEHOLDER,
  TriageCard,
  TriageLinkBall,
  TriageReconcileRow,
  formatAccountPath,
  INTER_ENTITY_ACCOUNTS,
  offsetLines,
  proposalSuggestedAccounts,
  type TriageRowBecomes,
  type TriageRowBusiness,
  type TriageRowLink,
} from "./TriageReconcileRow";
import {
  TriageDocumentPairs,
  type TriagePairedItem,
} from "./TriageDocumentPairs";
import {
  type TriageBulkMatch,
  type TriageLinedBusiness,
  toggleLinesSuggestion,
  useTriageDetailContext,
  useTriageViewActions,
} from "./TriageView.context";
import { TRIAGE_SETTLEMENTS } from "./TriageView.content";
import { TRIAGE_KEYS } from "./TriageView.keys";
import {
  TriageProposalCategoryChip,
  TriageSettlementChip,
  TriageSuggestionCategoryChip,
} from "./TriageChangelistProposalRow";

/**
 * The payments of one payee, booked or moved with one of them.
 *
 * A payment's payee's others - waiting, or booked before - are listed as the
 * queue's own cards: those it would change, each saying what it becomes, all
 * ticked, each business's under its name, narrowed by whether they are
 * booked, the bank account and how recent. Similar - the chip in the queue's
 * search, or L - lists them in the queue's place (``TriageBookLikeCards``),
 * and its suggestions become the bulk list of what is done to them all
 * (``TriageBookLikeChanges``), booked by the toolbar's Book; its Move lists
 * the same list, ticked as it was left, the ticked ones moved with it as each
 * is (``TriageMoveSettles``).
 */

/**
 * What the payment looked at is about to be booked as: the lines its Book
 * sends - its suggestion's, or the person's own - what they link, and how a
 * business they leave for settles it.
 */
export type TriageBookAs = {
  lines?: BookLineInput[];
  links?: BookLinksInput | null;
  treatment?: SettlementTreatmentEnum | null;
};

/** A payment's booking in parts, as the filters read it: as it is, or as it would be. */
export type TriageBookingParts = {
  businessIdentifier: string;
  businessName?: string | null;
  treatment?: SettlementTreatmentEnum | null;
  isBooked: boolean;
  accounts: string[];
  category?: string | null;
};

/**
 * One of the payee's others: the queue's card of it, what it is booked to now,
 * and what it becomes - unset, left as it is - in words and in parts. Asked for
 * its documents too: the one of its own it would be matched to, a draft or
 * not; else what a search of the mailboxes found for it to import - and
 * whether none is offered it at all, where the mailboxes may hold it.
 */
export type TriageBookLikePayment = {
  card: TriageListItemFragment;
  bookedAs: string | null;
  bookedAsLike: string | null;
  booked: TriageBookingParts;
  bookedLike: TriageBookingParts | null;
  document?: {
    identifier: string;
    name: string | null;
    isDraft: boolean;
  } | null;
  found?: {
    identifier: string;
    subject: string | null;
    filename: string | null;
    sentAt: string | null;
    amount: string | null;
    currency: string | null;
  } | null;
  isDocumentMissing?: boolean;
};

/** A card's height, as the queue draws it: the list is two and a half of them tall, the rest scrolled to. */
const CARD_HEIGHT = 104;
/** How many of the payee's others are drawn past the one the keys are on, the list first shown - and how many more with each step after. */
const TRIAGE_BOOK_LIKE_FIRST_DRAWN = 8;

/** The link from each of Similar's ticked payments to the post or move picked for them in the bulk list. */
const TRIAGE_BULK_PICK_LINK = "bulk-pick";

/** How long after the balls have faded in the rest start to be drawn, and between each few more. */
const TRIAGE_BOOK_LIKE_FILL_DELAY_MS = 60;

/** Loading, a typical one: the queue's own placeholder card, becoming what a payment is usually booked as. */
const PLACEHOLDER: TriageBookLikePayment = {
  card: TRIAGE_RECONCILE_ROW_PLACEHOLDER,
  bookedAs: null,
  bookedAsLike: "Meals & Entertainment",
  booked: { businessIdentifier: "", isBooked: false, accounts: [] },
  bookedLike: null,
};

/** How each business settles one it holds for another, in a word. */
const SETTLED_AS: Record<SettlementTreatmentEnum, string> = {
  LOAN: "Loan",
  OWNER_EQUITY: "Equity",
  DIVIDEND: "Dividend",
} as Record<SettlementTreatmentEnum, string>;

/**
 * What a booking is on each side the filters read it by: the
 * business it is in and how it settles there, whether it is booked, its
 * accounts, its category - each in the words its chip and its line say.
 */
export const TRIAGE_BOOKING_SIDES = {
  business: (parts: TriageBookingParts) =>
    parts.treatment
      ? `${parts.businessName ?? "Another business"} · ${SETTLED_AS[parts.treatment]}`
      : (parts.businessName ?? "This business"),
  status: (parts: TriageBookingParts) =>
    parts.isBooked ? "Booked" : "Not booked",
  account: (parts: TriageBookingParts) =>
    parts.accounts.length > 0 ? parts.accounts.join(", ") : "Uncoded",
  category: (parts: TriageBookingParts) => parts.category ?? "None",
  // Booked across businesses or not: in another than *own*, the business of
  // the payment's bank account.
  crossBusiness: (parts: TriageBookingParts, own?: string | null) =>
    parts.businessIdentifier && parts.businessIdentifier !== own
      ? "Cross-business"
      : "Same business",
} as const;

/** How far back a payment may be, by the name its chip says it with. */
const SINCE: Record<string, number> = {
  "Last 30 days": 30,
  "Last 90 days": 90,
  "Last year": 365,
};

/**
 * What the payee's others are narrowed by - each its chip, its values those
 * the payments have: which way the money went, booked or not, the business it
 * is in and how, its account, its category, its bank account and how recent.
 */
const BOOK_LIKE_FILTERS: {
  key: string;
  name: string;
  icon: LucideIcon;
  of: (row: TriageBookLikePayment) => string;
}[] = [
  {
    key: "direction",
    name: "Direction",
    icon: ArrowLeftRightIcon,
    of: (row) => (Number(row.card.amount ?? 0) < 0 ? "Money out" : "Money in"),
  },
  {
    key: "status",
    name: "Status",
    icon: CircleCheckIcon,
    of: (row) => TRIAGE_BOOKING_SIDES.status(row.booked),
  },
  {
    key: "business",
    name: "Cross-business",
    icon: iconsByTypename.EntityObject,
    of: (row) =>
      TRIAGE_BOOKING_SIDES.crossBusiness(
        row.booked,
        row.card.entity?.identifier,
      ),
  },
  {
    key: "account",
    name: "Account",
    icon: iconsByTypename.ChartedAccountObject,
    of: (row) => TRIAGE_BOOKING_SIDES.account(row.booked),
  },
  {
    key: "category",
    name: "Category",
    icon: icons.tags,
    of: (row) => TRIAGE_BOOKING_SIDES.category(row.booked),
  },
  {
    key: "bank",
    name: "Bank account",
    icon: iconsByTypename.FinancialAccountObject,
    of: (row) => row.card.financialAccount?.name ?? "None",
  },
  {
    key: "date",
    name: "Date",
    icon: CalendarIcon,
    of: () => "",
  },
];

/** How the list is narrowed and ticked, afresh for each payment looked at: its filters unset, those it opens with. */
export type TriageBookLikeView = {
  payment: string | null;
  unticked: Set<string>;
  filters: FilterType[] | null;
};

/** All ticked, as it opens with: its sets are never changed, only replaced. */
const FRESH_VIEW: Omit<TriageBookLikeView, "payment"> = {
  unticked: new Set(),
  filters: null,
};

/**
 * The others of *payment*'s payee, in every business the person is in, as it
 * is about to be booked (*bookAs*), its own business's first: those it would
 * change listed, narrowed and ticked, the ticked ones in sight booked with it;
 * those that cannot be booked like it, or are booked so already, left as they
 * are. Nothing picked to book it as yet (*bookAs* unset): every one of them,
 * as each is now. *withDocuments*, each with its own document - or what the
 * mailboxes hold for it - as Similar lines each up with its own.
 */
/**
 * What each of a payment's payee's others takes of it, asked with their own
 * documents: as its Book asks, as the payment opens - the one answer Similar
 * reads too.
 */
const TRIAGE_BOOK_LIKE_DOCUMENT_PARTS = [
  BookingPartEnum.Accounts,
  BookingPartEnum.Business,
  BookingPartEnum.Document,
];

export function useTriageBookLike(
  payment: {
    id: string;
    identifier: string;
    entityIdentifier?: string | null;
    amount?: string | number | null;
  } | null,
  bookAs: TriageBookAs | null,
  // Where how it is ticked and narrowed is kept: the store's for the payment
  // open in triage - its Book's and the node panel's one list - or a dialog's.
  [kept, keep]: readonly [
    TriageBookLikeView | null,
    (view: TriageBookLikeView) => void,
  ],
  withDocuments = false,
  // The queue's filter - its search and its queues - Similar's list narrowed
  // by it too, as the bar the two share says.
  narrowedBy: TransactionFilter | null = null,
) {
  const apolloClient = useApolloClient();
  const paymentId = payment?.id ?? null;
  const paymentIdentifier = payment?.identifier ?? null;
  // Those the queue's filter keeps - what was kept till the next answer is in,
  // so the list is not drawn whole between two.
  const narrowed = useBackendQuery(Queries.TRIAGE_PAYEE_OF_NARROWED_QUERY, {
    variables: { payment: paymentIdentifier ?? "", narrowedBy: narrowedBy! },
    skip: !paymentIdentifier || !narrowedBy,
  });
  const narrowedAnswer = narrowed.data ?? narrowed.previousData;
  const keptByQueue = useMemo(
    () =>
      narrowedBy && narrowedAnswer
        ? new Set(
            (narrowedAnswer.triageConnection?.data?.edges ?? []).map(
              ({ node }) => node.identifier,
            ),
          )
        : null,
    [narrowedAnswer, narrowedBy],
  );
  // Another payment looked at: its own list, all ticked, nothing narrowed.
  const view = useMemo(
    () =>
      kept?.payment === paymentIdentifier
        ? kept
        : { ...FRESH_VIEW, payment: paymentIdentifier },
    [kept, paymentIdentifier],
  );
  const isPicked = bookAs != null;
  // The card the keys are on, by its node - none till they are put on one:
  // ↑ ↓ move it, and Space or Enter ticks it or lets it go.
  const [keyed, setKeyed] = useState<string | null>(null);
  // Each as it is now, nothing picked to book it as: what the payment opens
  // with.
  const asItIs = {
    id: payment?.id ?? "",
    payment: payment?.identifier ?? "",
    lines: null,
    links: null,
    treatment: null,
    // Its booking whole, and each one's own document besides.
    parts: withDocuments ? TRIAGE_BOOK_LIKE_DOCUMENT_PARTS : null,
    documents: withDocuments,
  };
  const { data, previousData, loading, refetch } = useBackendQuery(
    Queries.TRIAGE_BOOK_LIKE_QUERY,
    {
      variables: {
        ...asItIs,
        lines: bookAs?.lines ?? null,
        links: bookAs?.links ?? null,
        treatment: bookAs?.treatment ?? null,
      },
      skip: !payment,
      // Asked once, as the payment opens - read by Similar too - and asked
      // again only as something is changed (``refetch``), or the queue's own
      // changes come in.
      fetchPolicy: "cache-first",
    },
  );
  // Booked another way while it is looked at - another suggestion picked - the
  // list it had stays until the new one is in: nothing comes and goes between.
  // Picked, till its answer is in, each as it is now where that was read
  // already: the cards at once, what each becomes after.
  const answered =
    data ??
    (payment != null && previousData?.node?.id === payment.id
      ? previousData
      : undefined);
  const asked =
    answered == null && payment != null && bookAs != null
      ? (apolloClient.readQuery({
          query: Queries.TRIAGE_BOOK_LIKE_QUERY,
          variables: asItIs,
        }) ?? undefined)
      : undefined;
  const answer = answered ?? asked;
  // Each shown till what it would become, booked as now picked, is in.
  const isAsAskedAhead = data == null && answer != null;
  const ownBusiness = payment?.entityIdentifier ?? null;

  return useMemo(() => {
    const setTicked = (identifiers: string[], isTicked: boolean) => {
      const unticked = new Set(view.unticked);
      for (const identifier of identifiers) {
        if (isTicked) unticked.delete(identifier);
        else unticked.add(identifier);
      }
      keep({ ...view, unticked });
    };
    const words = new Map(
      (answer?.node?.__typename === "TransactionObject"
        ? (answer.node.payeePayments ?? [])
        : []
      ).map((row) => [row.payment.identifier, row]),
    );
    const listed: TriageBookLikePayment[] = (
      answer?.triageConnection?.data?.edges ?? []
    )
      .flatMap(({ node: card }) => {
        const word = words.get(card.identifier);
        if (!word || card.identifier === paymentIdentifier) return [];
        const changes =
          word.bookedAsLike != null && word.bookedAs !== word.bookedAsLike;
        // Its own document: the one it would pay, or the likeliest the
        // mailboxes hold for it - one that prints what it paid.
        const document =
          withDocuments && word.document
            ? {
                identifier: word.document.identifier,
                name: word.document.name ?? null,
                isDraft: word.document.pendingChange != null,
              }
            : null;
        const result = withDocuments
          ? (word.payment.emailConnectionSearchResults ?? []).find(
              (one) => one.amountFound,
            )
          : undefined;
        const found =
          !document && result
            ? {
                identifier: result.identifier,
                subject: result.subject ?? null,
                filename: result.filename ?? null,
                sentAt: result.sentAt ?? null,
                amount: result.amount != null ? String(result.amount) : null,
                currency: result.currency ?? null,
              }
            : null;
        return [
          {
            card,
            bookedAs: word.bookedAs ?? null,
            bookedAsLike: changes ? word.bookedAsLike! : null,
            booked: word.booked,
            bookedLike: changes ? (word.bookedLike ?? null) : null,
            document,
            found,
            isDocumentMissing:
              withDocuments && !found && word.isDocumentMissing === true,
          },
        ];
      })
      // Its own business's first, then the others', each together.
      .sort(
        (left, right) =>
          Number(left.card.entity?.identifier !== ownBusiness) -
            Number(right.card.entity?.identifier !== ownBusiness) ||
          (left.card.entity?.name ?? "").localeCompare(
            right.card.entity?.name ?? "",
          ),
      );
    // Those it would change - or, nothing picked to book it as yet, every one,
    // as also while what each would become is still to come.
    const others =
      isPicked && !isAsAskedAhead
        ? listed.filter((row) => row.bookedAsLike != null)
        : listed;
    // What it opens with, read off the payment itself: the same way the money
    // went and booked or not as it is - and, of the business it is in, its
    // account and its category, each its booking leaves as it is.
    const itself = paymentIdentifier ? words.get(paymentIdentifier) : undefined;
    const presets: FilterType[] | null = itself
      ? [
          {
            key: "direction",
            type: "enum",
            operator: "exact",
            value: Number(payment?.amount ?? 0) < 0 ? "Money out" : "Money in",
          },
          {
            key: "status",
            type: "enum",
            operator: "exact",
            value: TRIAGE_BOOKING_SIDES.status(itself.booked),
          },
          // Its business, account and category, only once it is picked to
          // be booked some way: nothing picked, every one is listed as it is.
          ...(isPicked
            ? (["business", "account", "category"] as const)
            : []
          ).flatMap((side): FilterType[] => {
            const of = (parts: TriageBookingParts) =>
              side === "business"
                ? TRIAGE_BOOKING_SIDES.crossBusiness(parts, ownBusiness)
                : TRIAGE_BOOKING_SIDES[side](parts);
            const now = of(itself.booked);
            const then = itself.bookedLike ? of(itself.bookedLike) : now;
            return now === then
              ? [{ key: side, type: "enum", operator: "exact", value: now }]
              : [];
          }),
        ]
      : null;
    const filters = view.filters ?? presets ?? [];
    // Today, as the list's own dates are read: a day is its first ten characters.
    const today = new Date().toISOString().slice(0, 10);
    const passes = (row: TriageBookLikePayment) =>
      filters.every((filter) => {
        const value = filter.value;
        if (value == null || (Array.isArray(value) && value.length === 0)) {
          return true;
        }
        if (filter.key === "date") {
          const days = SINCE[String(value)];
          return (
            !days ||
            String(row.card.effectiveDate ?? "").slice(0, 10) >=
              new Date(Date.parse(today) - days * 86_400_000)
                .toISOString()
                .slice(0, 10)
          );
        }
        const of = BOOK_LIKE_FILTERS.find((one) => one.key === filter.key)?.of;
        if (!of) return true;
        const its = of(row);
        switch (filter.operator) {
          case "exact":
            return its === value;
          case "inList":
            return (value as string[]).includes(its);
          case "notInList":
            return !(value as string[]).includes(its);
          case "isNull":
            return its === "None" || its === "Uncoded";
          default:
            return true;
        }
      });
    const shown = others.filter(
      (row) =>
        passes(row) && (!keptByQueue || keptByQueue.has(row.card.identifier)),
    );
    const ticked = shown.filter(
      (row) => !view.unticked.has(row.card.identifier),
    );
    // Each a chip of its own, the payment's own value its words - on, the
    // list narrowed to the payments that have it too; off, outlined.
    const quickFilters = itself
      ? BOOK_LIKE_FILTERS.filter((one) => one.key !== "bank").map((one) => {
          const value =
            one.key === "direction"
              ? Number(payment?.amount ?? 0) < 0
                ? "Money out"
                : "Money in"
              : one.key === "date"
                ? "Last 90 days"
                : one.key === "business"
                  ? TRIAGE_BOOKING_SIDES.crossBusiness(
                      itself.booked,
                      ownBusiness,
                    )
                  : TRIAGE_BOOKING_SIDES[
                      one.key as "status" | "account" | "category"
                    ](itself.booked);
          return {
            key: one.key,
            name: one.name,
            icon: one.icon,
            value,
            isOn: filters.some((filter) => filter.key === one.key),
          };
        })
      : [];
    return {
      payment: paymentIdentifier,
      others,
      shown,
      ticked,
      view,
      filters,
      quickFilters,
      isLoading: loading && answer == null,
      // Read again: what a change made at once, a search or an import changed
      // of each.
      refetch,
      tick: (identifier: string, isTicked: boolean) =>
        setTicked([identifier], isTicked),
      keyed,
      setKeyed,
      // A chip pressed: its filter on, by the payment's own value, or off.
      toggleFilter: (key: string, value: string) =>
        keep({
          ...view,
          filters: filters.some((filter) => filter.key === key)
            ? filters.filter((filter) => filter.key !== key)
            : [...filters, { key, type: "enum", operator: "exact", value }],
        }),
      // Every filter off, as its Filter's Clear says.
      clearFilters: () => keep({ ...view, filters: [] }),
      // Whether the payment is picked to be booked some way: till it is,
      // nothing is staged on a card.
      isPicked,
      // The payment itself, as it is and as it is about to be booked.
      itself: itself
        ? { booked: itself.booked, bookedLike: itself.bookedLike ?? null }
        : null,
      // And as the queue's card of it, pinned over the rest: its row as the
      // queue read it (the payee's others leave it out).
      pinned: (() => {
        const card =
          itself && paymentId
            ? apolloClient.cache.readFragment<TriageListItemFragment>({
                id: apolloClient.cache.identify({
                  __typename: "TransactionObject",
                  id: paymentId,
                }),
                fragment: TriageListItemFragmentDoc,
                fragmentName: "TriageListItem",
                returnPartialData: true,
              })
            : null;
        return card?.identifier && itself
          ? ({
              card,
              bookedAs: itself.bookedAs ?? null,
              bookedAsLike: itself.bookedAsLike ?? null,
              booked: itself.booked,
              bookedLike: itself.bookedLike ?? null,
            } as TriageBookLikePayment)
          : null;
      })(),
    };
  }, [
    answer,
    apolloClient,
    isAsAskedAhead,
    isPicked,
    keep,
    keptByQueue,
    keyed,
    loading,
    ownBusiness,
    payment?.amount,
    paymentId,
    paymentIdentifier,
    refetch,
    view,
    withDocuments,
  ]);
}

export type TriageBookLike = ReturnType<typeof useTriageBookLike>;

/**
 * The payee's others booked with the payment, as the queue draws them: each
 * card saying what it becomes, ticked or not - by the ball on its edge, or a
 * click on it - narrowed by the chips over them.
 * A match picked in the bulk list, the same list - what each is lined up
 * with drawn level with its row in the panel (``TriageDocumentPairs``).
 */
// Drawn again only as what it is given changes: in the queue's place, nothing
// round it - the view's own changes, its balls fading in and out - redraws
// its rows.
export const TriageBookLikeCards = memo(function TriageBookLikeCards({
  bookLike,
  fill = false,
  isSource = false,
  hasFilterBar = true,
  onOpenPanel,
  lining = null,
  linedUp = null,
  roomBelow = 0,
  pick = null,
}: {
  /** Room past its last row, to scroll on to what is lined up beyond it. */
  roomBelow?: number;
  /** The bulk list's row picked (``bulkPick``): a post staged on each card, or a move on the halves of the payment and those ticked. None, nothing staged. */
  pick?: string | null;
  bookLike: TriageBookLike;
  /** The match picked in the bulk list, if one is: what its cards are lined up for. */
  lining?: TriageBulkMatch | null;
  /** What each is lined up with, by its business: one across two businesses draws the other's half. */
  linedUp?: ReadonlyMap<string, TriageLinedBusiness> | null;
  /** Its own bar of filters over it - not in the queue's place, where the queue's bar is its. */
  hasFilterBar?: boolean;
  /** All the height it is given, the list scrolling in it - else two and a half cards tall. */
  fill?: boolean;
  /** The payment pinned among them is what their changes are made from, not one done first. */
  isSource?: boolean;
  /** Where a card's Open in panel opens it, in place of the app's panel - ``null``, it has none. */
  onOpenPanel?: ((identifier: string) => void) | null;
}) {
  const { shown, view } = bookLike;
  // The queue's own rows' size.
  const size = "md";
  // The payment itself, pinned over the rest - loading, the queue's own
  // placeholder in its place, so the list is not pushed down as it comes.
  const pinned = bookLike.pinned ?? (bookLike.isLoading ? PLACEHOLDER : null);
  // What a card says it becomes, as the change picked leaves it: a move,
  // staged on the halves of the payment and those ticked - the business it is
  // for and how the two settle it; or booked like the payment, its category
  // chip - what it is filed under now and booked like it, the change shown
  // only where there is one - and the business it would be booked in, on its
  // half. Its own document beside either.
  const entities = State((state) => state.entities);
  const move = pick?.startsWith("move:") ? pick : null;
  const isPostPicked = pick != null && !move;
  const [, movedTo, movedAs] = move?.split(":") ?? [];
  const movedToEntity = entities.find(
    (entity) => entity.identifier === movedTo,
  );
  const becomesOf = (
    row: TriageBookLikePayment,
    isMoved: boolean,
  ): TriageRowBecomes => {
    const own = row.card.entity?.identifier ?? null;
    // Another business it is for now, as its row says.
    const forNow: TriageRowBusiness | null = row.card.belongsToEntity
      ? {
          id: row.card.belongsToEntity.id,
          identifier: row.card.belongsToEntity.identifier,
          name: row.card.belongsToEntity.name ?? null,
          treatment: row.card.settlementTreatment ?? null,
        }
      : null;
    const document = row.document
      ? {
          label: `↔ ${row.document.name ?? "Its document"}${row.document.isDraft ? " · draft" : ""}`,
        }
      : row.found
        ? {
            label: `In mailbox · ${row.found.subject ?? row.found.filename ?? "an email"}`,
          }
        : null;
    if (movedToEntity) {
      return {
        now: null,
        like: null,
        business: {
          now: forNow,
          // Moved with it, it is for that business - but one of it already.
          like:
            isMoved && movedToEntity.identifier !== own
              ? {
                  id: movedToEntity.id,
                  identifier: movedToEntity.identifier,
                  name: movedToEntity.name ?? null,
                  treatment: (movedAs as SettlementTreatmentEnum) ?? null,
                }
              : forNow,
        },
        document,
      };
    }
    const now = row.booked.isBooked
      ? (row.booked.category ?? (row.booked.accounts.join(", ") || null))
      : null;
    const like = row.bookedLike
      ? (row.bookedLike.category ??
        (row.bookedLike.accounts.join(", ") || null))
      : null;
    const bookedIn = row.bookedLike?.businessIdentifier ?? null;
    return {
      now,
      like: like === now ? null : like,
      business: {
        now: forNow,
        like:
          row.bookedLike && bookedIn && bookedIn !== own
            ? {
                id: identifierAndTypenameToId(bookedIn, "EntityObject"),
                identifier: bookedIn,
                name: row.bookedLike.businessName ?? null,
                treatment: row.bookedLike.treatment ?? null,
              }
            : row.bookedLike
              ? null
              : forNow,
      },
      document,
    };
  };
  // A match picked: what each is lined up with, in its business - one across
  // two businesses draws the other's half.
  const findLinedUp = (card: TriageListItemFragment) =>
    lining
      ? { kind: lining, business: linedUp?.get(card.identifier) ?? null }
      : null;
  // Each card's ball on its right edge - the end of the line to what is lined
  // up with it, nothing lined up whether it is ticked: pressed, it is let go,
  // or ticked again. The payment itself is always in, its ball solid.
  // Pressed, read as it is then: a card is drawn again only as what it shows
  // changes, and what it was given to press with as it was drawn last.
  const bookLikeRef = useRef(bookLike);
  bookLikeRef.current = bookLike;
  const findLink = (row: TriageBookLikePayment): TriageRowLink => {
    const isItself = row.card.identifier === pinned?.card.identifier;
    const isTicked = isItself || !view.unticked.has(row.card.identifier);
    return {
      // To what is lined up with it; else, a post or a move picked in the
      // bulk list, to that - one line from each ticked.
      link:
        lining || pick == null ? row.card.identifier : TRIAGE_BULK_PICK_LINK,
      end: "from",
      isEngaged: isTicked,
      onPress: isItself
        ? undefined
        : () =>
            bookLikeRef.current.tick(
              row.card.identifier,
              bookLikeRef.current.view.unticked.has(row.card.identifier),
            ),
      label: isItself
        ? isSource
          ? "What the changes are made from"
          : "This payment, first"
        : (row.card.description ?? undefined),
      testId: "triage-book-like-ball",
    };
  };
  // The payment itself, pinned: what is done first, or what the changes are
  // made from.
  const pinnedCard = pinned ? (
    <div
      data-testid="triage-book-like-itself"
      data-identifier={pinned.card.identifier}
    >
      <TriageReconcileRow
        item={pinned.card}
        isLoading={bookLike.pinned == null}
        onOpenPanel={onOpenPanel}
        becomes={
          (isPostPicked && bookLike.isPicked) || movedToEntity
            ? becomesOf(pinned, true)
            : undefined
        }
        linedUp={findLinedUp(pinned.card)}
        link={bookLike.pinned == null ? null : findLink(pinned)}
      />
    </div>
  ) : null;
  const renderCard = (row: TriageBookLikePayment, isLoading = false) => {
    const isTicked = !view.unticked.has(row.card.identifier);
    return (
      <div
        data-testid="triage-book-like-row"
        data-identifier={row.card.identifier}
        data-ticked={isTicked}
      >
        <TriageReconcileRow
          item={row.card}
          isLoading={isLoading}
          onOpenPanel={onOpenPanel}
          linedUp={findLinedUp(row.card)}
          link={isLoading ? null : findLink(row)}
          // What each would be booked as, while that is what is picked - not
          // while a match is open beside them, which is not booked so.
          becomes={
            ((isPostPicked && bookLike.isPicked) || movedToEntity) && !lining
              ? becomesOf(row, isTicked)
              : undefined
          }
          // In Similar, each is one of the payee's others: none counts them.
        />
      </div>
    );
  };
  // The payment itself among the rest, newest first - its business's first,
  // as theirs are - each ticked or let go but it, which is what they are
  // booked like.
  const pinnedIdentifier = bookLike.pinned?.card.identifier ?? null;
  const own = bookLike.pinned?.card.entity?.identifier ?? null;
  const listed = bookLike.pinned
    ? [...shown, bookLike.pinned].sort(
        (left, right) =>
          Number(left.card.entity?.identifier !== own) -
            Number(right.card.entity?.identifier !== own) ||
          String(right.card.effectiveDate ?? "").localeCompare(
            String(left.card.effectiveDate ?? ""),
          ),
      )
    : shown;
  // Drawn down to a screenful past the one the keys are on first, the room
  // the rest take kept under them, and those after it a few more at a time
  // once it is up, as there is time: only rows out of sight under the rest
  // come in later, so nothing in sight moves for them. A key pressed before
  // then draws them all at once, so the keys never run out.
  const keyedAt = Math.max(
    0,
    listed.findIndex(
      (row) =>
        row.card.id === bookLike.keyed ||
        (bookLike.keyed == null && row.card.identifier === pinnedIdentifier),
    ),
  );
  const [drawnMore, setDrawnMore] = useState(0);
  const drawnTo = Math.min(
    listed.length,
    keyedAt + TRIAGE_BOOK_LIKE_FIRST_DRAWN + 1 + drawnMore,
  );
  const isWhole = drawnTo === listed.length;
  const [wasWhole, setWasWhole] = useState(false);
  useEffect(() => {
    if (isWhole) {
      // Whole once read: none of its rows is put in while it is loading.
      if (!bookLike.isLoading) setWasWhole(true);
      return;
    }
    const timeout = setTimeout(
      () =>
        startTransition(() =>
          setDrawnMore((was) => was + TRIAGE_BOOK_LIKE_FIRST_DRAWN),
        ),
      drawnMore === 0
        ? TRIAGE_LINK_BALL_FADE_MS + TRIAGE_BOOK_LIKE_FILL_DELAY_MS
        : TRIAGE_BOOK_LIKE_FILL_DELAY_MS,
    );
    return () => clearTimeout(timeout);
  }, [bookLike.isLoading, drawnMore, isWhole]);
  const drawn = isWhole ? listed : listed.slice(0, drawnTo);
  const roomUnder = (listed.length - drawnTo) * CARD_HEIGHT + roomBelow;
  const toggle = (row: TriageBookLikePayment) => {
    if (row.card.identifier === pinnedIdentifier) return;
    bookLike.tick(row.card.identifier, view.unticked.has(row.card.identifier));
  };
  return (
    <div
      data-testid="triage-book-like"
      onKeyDownCapture={() => {
        if (!isWhole) flushSync(() => setDrawnMore(listed.length));
      }}
      className={cn("flex flex-col min-w-0", fill && "h-full min-h-0")}
    >
      {/* Its filter bar, as the queue's toolbar is: what they are narrowed to
          - set as the payment itself is, on all it keeps as it is. In the
          queue's place, the queue's bar is its, its filters in its Filter. */}
      {!hasFilterBar ? null : (
        <div
          data-testid="triage-book-like-filters"
          // One line, the bar's height - as the panel's bar beside it is, so
          // what is lined up under the two starts level - scrolled across
          // where it is too narrow for them all.
          className="shrink-0 flex items-center gap-1 h-12 pl-2 pr-2 box-border bg-background [border-bottom-width:1px] [border-bottom-style:solid] border-border"
        >
          {/* Each the payment's own, one chip a filter: set, its outline
            filled; unset, dashed. Pressed, on or off. Scrolled across where
            there is no room for them all, what is at its end staying there.
            In the queue's place, these are in the queue's own Filter. */}
          <div className="flex-1 min-w-0 flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {bookLike.quickFilters.map((chip) => {
              const toggle = () => bookLike.toggleFilter(chip.key, chip.value);
              return (
                <Tooltip key={chip.key}>
                  <TooltipTrigger asChild>
                    <Toggle
                      variant="outline"
                      size="sm"
                      pressed={chip.isOn}
                      onPressedChange={toggle}
                      data-testid={`triage-book-like-filter-${chip.key}`}
                      // Its press is its own, not the dialog's keys'.
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.stopPropagation();
                        }
                      }}
                      className="shrink-0 max-w-full data-[state=off]:border-dashed data-[state=off]:text-muted-foreground"
                    >
                      <Icon
                        ReactIcon={chip.icon}
                        className="text-muted-foreground"
                      />
                      {chip.value}
                    </Toggle>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{chip.name}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      )}
      {/* The payments, one list on the queue's own ground - the room after
          its last row its list draws said here (``--room-under``). */}
      <div
        style={{ "--room-under": `${roomUnder}px` } as CSSProperties}
        className={cn(
          "bg-muted dark:bg-[#151719]",
          fill && "flex-1 min-h-0 flex flex-col",
        )}
      >
        {
          <SelectableCardList
            // The payment itself among them, where its date puts it; the one
            // the keys are on held in sight at the top or the foot as they
            // scroll past it.
            items={drawn}
            // The rest come in where the room was kept for them.
            isFillingIn={drawnMore > 0 && !wasWhole}
            loading={bookLike.isLoading}
            placeholderItem={PLACEHOLDER}
            getItemKey={(row) => row.card.id}
            // The keys on a card - ↑ ↓ move them - ringed as a list's row is.
            isItemActive={(row) => row.card.id === bookLike.keyed}
            scrollToActiveKey={bookLike.keyed}
            onItemNavigate={(row) => bookLike.setKeyed(row.card.id)}
            // A click on a card ticks it, or lets it go, the keys on it: it is
            // not opened here - as Space and Enter do on the one they are on.
            onItemClick={(row) => {
              bookLike.setKeyed(row.card.id);
              toggle(row);
            }}
            onItemSpace={toggle}
            onItemEnter={toggle}
            stickyActiveRow
            keyboardScope="nearest"
            disableVirtualization
            // Booked or matched, one folds away - one a chip leaves out goes
            // at once.
            animatesRemovals
            listKey={JSON.stringify(bookLike.filters)}
            variableItemHeight
            estimatedItemHeight={CARD_HEIGHT}
            size={size}
            variant="outlined"
            layout="cards"
            rowHover="title"
            data-testid="triage-book-like-list"
            // Drawn as the queue draws its rows: a hairline between them, and
            // a card only round the one the keys are on.
            className={cn(
              // Two and a half cards tall (``CARD_HEIGHT``), the rest scrolled to.
              fill ? "flex-1 min-h-0" : "flex-none max-h-65",
              // Room after its last row to scroll on into, and the rows not
              // drawn yet: not padding, which would stretch the list rather
              // than let it scroll.
              roomUnder > 0 &&
                "[&::after]:[content:''] [&::after]:block [&::after]:h-(--room-under)",
            )}
            renderItem={({ item: row, isLoading }) =>
              !isLoading && row.card.identifier === pinnedIdentifier
                ? pinnedCard
                : renderCard(row, isLoading)
            }
          />
        }
      </div>
    </div>
  );
});

/** What a change made at once came to, in words: done, or each refused and why. */
type TriageChangeAnswer = {
  done: string[];
  refused: { transaction: string; reason: string }[];
};

/**
 * A row of Similar's bulk list: one of the three matches made of every ticked
 * payment that has one - its transfer's other side, the payment it copies, its
 * own document - or an account its suggestion posts the payment to, which
 * every ticked one is booked like.
 */
type TriageBulkRow = {
  key: string;
  kind: "transfers" | "duplicates" | "documents" | "post" | "move";
  /** What it does, before what it does it to: "Match", "Post to", "Move to". */
  lead: string;
  /** What it does it to, as loud as a title. */
  title: string;
  note: string;
  /** How many of the ticked it would book - only on a match, which opens a view of its own. */
  count: number | null;
  /** The suggestion it posts by, for a row of accounts. */
  suggestionIdentifier?: string;
  /** What it files them under, by name - made, or to be made as it is booked - where it says. */
  categoryName?: string | null;
  /** The account it posts to by hand, for one found by the words searched for. */
  chartedAccountIdentifier?: string;
  /** The category it files under there, where the words found the account by one of its categories. */
  category?: {
    id: string;
    identifier: string;
    name?: string | null;
    color?: TagColorEnum | null;
  } | null;
  /** An account not made yet, found by the words searched for: made in the business their cost is in as it is picked. */
  way?: {
    catalogueKey: string | null;
    chartedAccountIdentifier: string | null;
    categoryName: string | null;
  };
  /** Moved to another business: each way the two may settle them, on its card's chip. */
  moves?: TriageBulkMove[];
};

/** One way to move them to another business, on its card's chip. */
type TriageBulkMove = {
  key: string;
  business: string;
  treatment: SettlementTreatmentEnum;
  title: string;
  description: string;
  isRecommended: boolean;
  /** The payment open is moved there so already: nothing to do. */
  isDone: boolean;
};

/** Loading, a typical row: an account the payment would be posted to. */
const BULK_PLACEHOLDER: TriageBulkRow = {
  key: "placeholder",
  kind: "post",
  lead: "Post to",
  title: "Meals & Entertainment",
  note: "As the payments like it were booked",
  count: 3,
};

/** A suggestion as the bulk list reads it for a match: its kind, whether it waits, and the other side it offers. */
type TriageCounterpartSuggestion = {
  identifier: string;
  job?: string | null;
  status: string;
  ruleSuggestionTransactionOptions?:
    | {
        transaction?: {
          identifier: string;
          description?: string | null;
          amount?: unknown;
          effectiveDate?: unknown;
          entity?: { identifier: string } | null;
        } | null;
      }[]
    | null;
};

/** The jobs a match row books by, each by the suggestion of that kind the payment has. */
const BULK_MATCH_JOBS = {
  transfers: "TRANSFER",
  duplicates: "DUPLICATE",
} as const;

/**
 * Similar's bulk list, in the suggestions' place: what is done to the payment
 * open and its payee's others ticked in the queue, all at once - each a row,
 * the one picked what the toolbar's Book does. At its top, three matches:
 * each ticked one's transfer booked with its other side, each copy marked a
 * duplicate, and each lined up with its own document - picked, the line-up
 * in its place, beside the list in the queue's place (``TriageDocumentPairs``).
 * Under them, the accounts its
 * suggestions post it to: picked, each ticked card says what it becomes, and
 * Book books those it would change like it, then the payment itself.
 */
export function TriageBookLikeChanges({
  terms,
}: {
  /** The words searched for in the panel's search, by card - those for every card under ``all``. */
  terms: Record<string, string[]>;
}) {
  const apolloClient = useApolloClient();
  const { removeFromQueue, holdOffQueue, onResolved } = useTriageViewActions();
  const {
    triage,
    selections,
    setSelection,
    bookLikeOffer,
    setBulkBook,
    bulkMatch = null,
    setBulkMatch,
    bulkPick = null,
    setBulkPick,
    similarListElement = null,
    isSuggestionsKeyboardActive,
    suggestionsEngaged,
    movePayment,
    movingPaymentTo,
    journalSettlement,
  } = useTriageDetailContext();
  const entities = State((state) => state.entities);
  const bookLike = bookLikeOffer?.bookLike ?? null;
  const bookAs = bookLikeOffer?.bookAs ?? null;
  const ticked = bookLike?.ticked ?? [];

  // Each ticked one with a waiting suggestion of a match's kind - and the
  // payment open itself, by its own.
  const withJob = (job: string) => [
    ...(triage.suggestions ?? []).flatMap((suggestion) =>
      suggestion.job === job && suggestion.status === "PENDING"
        ? [{ payment: triage.identifier, suggestion: suggestion.identifier }]
        : [],
    ),
    ...ticked.flatMap((row) => {
      const suggestion = (row.card.suggestions ?? []).find(
        (one) => one.job === job && one.status === "PENDING",
      );
      return suggestion
        ? [{ payment: row.card.identifier, suggestion: suggestion.identifier }]
        : [];
    }),
  ];
  const transfers = withJob(BULK_MATCH_JOBS.transfers);
  const duplicates = withJob(BULK_MATCH_JOBS.duplicates);
  const withDocuments = ticked.filter((row) => row.document);
  // Those ticked it would book differently, booked like it.
  const toBook = ticked.filter((row) => row.bookedAsLike != null);

  // The accounts its suggestions post it to, the surest first: those it would
  // be booked like.
  const posts = (triage.suggestions ?? [])
    .filter(
      (suggestion) =>
        (suggestion.job === "CATEGORIZE" || suggestion.job === "SPLIT") &&
        suggestion.status === "PENDING" &&
        (suggestion.lines ?? []).length > 0,
    )
    // The one picked first - of several posting to one account, its row is
    // the one listed, picked as Similar opens on it - then the surest.
    .sort(
      (left, right) =>
        Number(right.identifier === selections.linesSuggestionIdentifier) -
          Number(left.identifier === selections.linesSuggestionIdentifier) ||
        (right.confidence ?? 0) - (left.confidence ?? 0),
    );
  // Opened, nothing is picked to book them as - nor staged on their cards -
  // till the person picks it: they may not want it posted at all.
  const matchRows: TriageBulkRow[] = [
    {
      key: "transfers",
      kind: "transfers",
      lead: "Match",
      title: "transfers",
      note:
        transfers.length > 0
          ? "Each booked with its other side"
          : "None of them is a transfer",
      count: transfers.length,
    },
    {
      key: "duplicates",
      kind: "duplicates",
      lead: "Match",
      title: "duplicates",
      note:
        duplicates.length > 0
          ? "Each marked a copy of the one it repeats"
          : "None of them is a copy",
      count: duplicates.length,
    },
    {
      key: "documents",
      kind: "documents",
      lead: "Match",
      title: "documents",
      note:
        withDocuments.length > 0
          ? "Each lined up with its own, by hand"
          : "None of them is offered a document",
      count: withDocuments.length,
    },
  ];
  const accountRows = posts
    .map((suggestion): TriageBulkRow => {
      // What it posts to, as its card names it - not its bank's line, nor
      // what one business owes another for it - and where: the business the
      // first is in, and where it sits in that business's chart.
      const accounts = proposalSuggestedAccounts(suggestion, triage);
      const posted = offsetLines(suggestion, triage).find(
        (line) =>
          !INTER_ENTITY_ACCOUNTS.has(line.chartedAccount?.name?.trim() ?? ""),
      );
      return {
        key: `post:${suggestion.identifier}`,
        kind: "post",
        lead: "Post to",
        title: accounts.map((account) => account.name).join(", "),
        categoryName:
          suggestion.ruleSuggestionChartedAccountOption?.category?.name ??
          suggestion.newCategoryName ??
          null,
        // Whose and where alone - nothing of the rule that offered it, nor how
        // sure it was.
        note: [posted?.entity?.name, accounts[0]?.path]
          .filter(Boolean)
          .join(" / "),
        // Not a view of its own: no count - only the matches, which open one.
        count: null,
        suggestionIdentifier: suggestion.identifier,
      };
    })
    // One row an account - the surest suggestion of it - and none naming
    // none.
    .filter(
      (row, index, all) =>
        row.title !== "" &&
        all.findIndex((one) => one.title === row.title) === index,
    );
  // Any account of the business its cost is in that the words searched for
  // find - as the posting card's search finds them - a row to post them to by
  // hand, under those its suggestions post it to. One not made yet is the
  // posting card's: made as it is booked there.
  const words = [...(terms.all ?? []), ...(terms.post ?? [])].join(" ");
  const bookedIn =
    journalSettlement?.settlementEntityIdentifier ??
    triage.entity?.identifier ??
    null;
  const { mutate: makeWayToBook } = useBackendMutation({
    mutation: Mutations.MAKE_WAY_TO_BOOK,
  });
  const foundWays = useBackendQuery(Queries.WAYS_TO_BOOK_QUERY, {
    variables: { entityIdentifier: bookedIn ?? "", words },
    skip: !words || !bookedIn,
    fetchPolicy: "cache-first",
  });
  // Where a found one posts, and nothing else: the business their cost is in,
  // and where its account sits in that business's chart.
  const businessName = entities.find(
    (entity) => entity.identifier === bookedIn,
  )?.name;
  const foundRows = (words ? (foundWays.data?.waysToBook?.data ?? []) : [])
    .map((way): TriageBulkRow => {
      const account = way.chartedAccount;
      // Its own: the account - filed under the category the words found it
      // by, or one they name that it has not, made as it is booked - as the
      // posting card's finds are.
      if (account?.identifier) {
        return {
          key: `account:${account.identifier}`,
          kind: "post",
          lead: "Post to",
          title: account.name ?? way.accountName,
          note: [businessName, formatAccountPath(account.fullPath)]
            .filter(Boolean)
            .join(" / "),
          count: null,
          chartedAccountIdentifier: account.identifier,
          category: way.category
            ? {
                ...way.category,
                color: (way.category.color ?? null) as TagColorEnum | null,
              }
            : null,
          categoryName: way.category?.name ?? way.categoryName ?? null,
        };
      }
      // The catalogue's account, not made yet: said so in its name, under the
      // business's Expenses, where it is made as it is picked.
      return {
        key: `way:${way.key}`,
        kind: "post",
        lead: "Post to",
        title: `${way.accountName} (new)`,
        note: [businessName, "Expenses"].filter(Boolean).join(" / "),
        count: null,
        categoryName: way.categoryName ?? null,
        way: {
          catalogueKey: way.catalogueKey ?? null,
          chartedAccountIdentifier: null,
          categoryName: way.categoryName ?? null,
        },
      };
    })
    // None its suggestions post it to already, filed as they would file it or
    // under nothing in particular - then one row an account of the rest, the
    // first way through it found.
    .filter(
      (row) =>
        !accountRows.some(
          (one) =>
            one.title === row.title &&
            (row.categoryName == null || one.categoryName === row.categoryName),
        ),
    )
    .filter(
      (row, index, all) =>
        all.findIndex((one) => one.key === row.key) === index,
    );
  // A match picked - here, or by the search's chip - lets go of whatever the
  // bulk list had picked.
  useEffect(() => {
    if (bulkMatch) setBulkPick?.(null);
  }, [bulkMatch, setBulkPick]);
  // Under them, a card for each of the person's other businesses - "Move to"
  // it - how the two settle them on its chip, the recommended way till
  // another is chosen there: picked, Book moves the payment and those ticked
  // with it (``move_transactions``). Where the payment open is moved so
  // already, that way is done. None till the payer is read: each way says how
  // the two settle it in their names.
  const payerName = triage.entity?.name;
  const isOut = Number(triage.amount) < 0;
  const moveRows = !payerName
    ? []
    : entities
        .filter((entity) => entity.identifier !== triage.entity?.identifier)
        .map(
          (entity): TriageBulkRow => ({
            key: `move:${entity.identifier}`,
            kind: "move",
            lead: "Move to",
            title: entity.name,
            note: "How the two settle them",
            // Not a view of its own: no count.
            count: null,
            moves: TRIAGE_SETTLEMENTS.filter(
              (settlement) => !isOut || !settlement.isTakenInOnly,
            ).map((settlement) => ({
              key: `move:${entity.identifier}:${settlement.treatment}`,
              business: entity.identifier,
              treatment: settlement.treatment,
              title: settlement.title,
              isRecommended: settlement.isRecommended ?? false,
              description: isOut
                ? settlement.describe(payerName, entity.name)
                : settlement.describe(entity.name, payerName),
              isDone:
                triage.belongsToEntity?.identifier === entity.identifier &&
                triage.settlementTreatment === settlement.treatment,
            })),
          }),
        );
  const rows: TriageBulkRow[] = [
    ...matchRows,
    ...accountRows,
    ...foundRows,
    ...moveRows,
  ];
  const moves = moveRows.flatMap((row) => row.moves ?? []);
  // The row picked: a match - its chip in the panel's search - or what was
  // picked here, while it is still listed.
  const picked =
    bulkMatch ??
    ([...rows.map((row) => row.key), ...moves.map((move) => move.key)].includes(
      bulkPick ?? "",
    )
      ? bulkPick
      : null);
  // The row the keys are on once → brings them in - from the one picked, or
  // the first: ↑ ↓ step through the rows, and only Enter - or a click - does
  // what one says. Out of them, the one picked is drawn as the keys' row is.
  const [keyed, setKeyed] = useState<string | null>(null);
  const keyedRow = suggestionsEngaged
    ? (keyed ?? picked ?? rows[0]?.key ?? null)
    : picked;
  // A move card's way of settling: the one picked on it, else the one
  // recommended that is not done already, else the first that is not.
  const findMoveOf = (row: TriageBulkRow) =>
    row.moves?.find((one) => one.key === picked) ??
    row.moves?.find((one) => one.isRecommended && !one.isDone) ??
    row.moves?.find((one) => !one.isDone);
  // *filedAs*, where given: the category chosen on a post's chip, or one
  // named there, in place of what the row files them under.
  const pick = (
    row: TriageBulkRow,
    stop?: string | null,
    filedAs?: {
      categoryIdentifier: string | null;
      newCategoryName: string | null;
    },
  ) => {
    if (row.kind === "move") {
      // The way chosen on its chip - or, the card itself, its own.
      const move =
        row.moves?.find((one) => one.key === stop) ?? findMoveOf(row);
      if (!move || move.isDone) return;
      setBulkMatch?.(null);
      setBulkPick?.(move.key);
      return;
    }
    if (row.kind !== "post") {
      setBulkMatch?.(row.kind);
      return;
    }
    // Found by the search: posted to it by hand, as the posting card's finds
    // are (``commitActiveOption``) - filed under the category the words found
    // it by, or one they name, made as it is booked - one not made yet made
    // first, in the business their cost is in, then posted to.
    const postTo = (
      account: string,
      category: string | null,
      newCategory: string | null,
    ) => {
      setSelection("linesSuggestionIdentifier", null);
      setSelection("chartedAccountIdentifier", account);
      setSelection("categoryIdentifier", category);
      setSelection("newCategoryName", newCategory);
    };
    if (row.chartedAccountIdentifier) {
      setBulkMatch?.(null);
      setBulkPick?.(row.key);
      postTo(
        row.chartedAccountIdentifier,
        filedAs
          ? filedAs.categoryIdentifier
          : (row.category?.identifier ?? null),
        filedAs
          ? filedAs.newCategoryName
          : row.category
            ? null
            : (row.categoryName ?? null),
      );
      return;
    }
    if (row.way && bookedIn) {
      setBulkMatch?.(null);
      const named = filedAs?.newCategoryName ?? null;
      void makeWayToBook({
        variables: {
          input: {
            entityIdentifier: bookedIn,
            ...row.way,
            // Named on its chip: that one made as it is booked, not the one
            // it would make.
            ...(named ? { categoryName: null } : {}),
          },
        },
        refetchQueries: ["TriageChartedAccountCatalog", "WaysToBook"],
        awaitRefetchQueries: true,
        callbacks: {
          onSuccess: (made) => {
            const answer = made as {
              chartedAccount?: { identifier: string } | null;
              category?: { identifier: string } | null;
            } | null;
            if (!answer?.chartedAccount) return;
            // Made, it is the business's own: listed, and picked, as such.
            setBulkPick?.(`account:${answer.chartedAccount.identifier}`);
            postTo(
              answer.chartedAccount.identifier,
              named ? null : (answer.category?.identifier ?? null),
              named,
            );
          },
        },
      });
      return;
    }
    const suggestion = posts.find(
      (one) => one.identifier === row.suggestionIdentifier,
    );
    if (!suggestion) return;
    setBulkMatch?.(null);
    setBulkPick?.(row.key);
    // Booked as it says - picked afresh even where the payment had it picked
    // already, so nothing picked by hand stays beside it.
    toggleLinesSuggestion(suggestion, null, setSelection);
  };

  const refetchQueue = () =>
    void apolloClient.refetchQueries({
      include: [Queries.TRIAGE_CONNECTION_QUERY],
    });
  // The one change being made, Book at work till it is answered.
  const [making, setMaking] = useState<string | null>(null);
  const { mutate: bookLikeIt } = useBackendMutation({
    mutation: Mutations.BOOK_LIKE,
  });
  const { mutate: bookViaSuggestion } = useBackendMutation({
    mutation: Mutations.BOOK_VIA_SUGGESTION,
  });
  // Booked like it: those ticked it would change, off the queue as it is
  // pressed - then the payment itself, by its own Book.
  const bookAll = () => {
    if (making || !bookLikeOffer?.canBookItself) return;
    if (toBook.length > 0 && triage.identifier) {
      const identifiers = toBook.map((row) => row.card.identifier);
      const release = holdOffQueue(identifiers);
      setMaking("post");
      bookLikeIt({
        variables: {
          input: {
            transactionIdentifiers: identifiers,
            like: triage.identifier,
            ...(bookAs?.lines
              ? {
                  lines: bookAs.lines,
                  links: bookAs.links ?? null,
                  treatment: bookAs.treatment ?? null,
                }
              : {}),
          },
        },
        callbacks: {
          onSuccess: (answer: TriageChangeAnswer) => {
            removeFromQueue(answer.done);
            release();
            setMaking(null);
            if (answer.refused.length > 0) refetchQueue();
            State.getState().setSnackbarMessage(
              answer.refused.length === 0
                ? `Booked ${answer.done.length + 1} payments.`
                : `Booked ${answer.done.length + 1}. ${answer.refused.length} not: ${answer.refused[0].reason}`,
            );
          },
          onError: () => {
            release();
            setMaking(null);
            refetchQueue();
          },
        },
      });
    }
    bookLikeOffer.onBook();
  };
  // Each transfer or copy booked by its own suggestion - the payment open
  // among them gone on from, the rest off the queue at once.
  const matchEach = (
    kind: "transfers" | "duplicates",
    pairs: { payment: string; suggestion: string }[],
  ) => {
    if (making || pairs.length === 0) return;
    const others = pairs
      .map((pair) => pair.payment)
      .filter((payment) => payment !== triage.identifier);
    const release = holdOffQueue(others);
    setMaking(kind);
    let left = pairs.length;
    const refused: string[] = [];
    const answered = (pair: { payment: string }, error?: boolean) => {
      if (error) refused.push(pair.payment);
      left -= 1;
      if (left > 0) return;
      release();
      setMaking(null);
      removeFromQueue(others.filter((one) => !refused.includes(one)));
      if (refused.length > 0) refetchQueue();
      const done = pairs.length - refused.length;
      State.getState().setSnackbarMessage(
        `${kind === "transfers" ? "Matched" : "Marked"} ${done} ${
          kind === "transfers" ? "transfers" : "duplicates"
        }.${refused.length > 0 ? ` ${refused.length} not.` : ""}`,
      );
    };
    for (const pair of pairs) {
      bookViaSuggestion({
        variables: { input: { suggestionIdentifier: pair.suggestion } },
        callbacks: {
          onSuccess: () => answered(pair),
          onError: () => answered(pair, true),
        },
      });
    }
    // The payment open among them: on to the next at once.
    if (pairs.some((pair) => pair.payment === triage.identifier)) {
      onResolved({ alsoRemoveTransactionIdentifiers: others });
    }
  };
  // What the line-up would match, and its Match - told by it.
  const [lined, setLined] = useState<{
    count: number;
    isMatching: boolean;
    match: () => void;
  } | null>(null);
  const onLined = useCallback(
    (now: { count: number; isMatching: boolean; match: () => void }) =>
      setLined(now),
    [],
  );
  useEffect(() => {
    if (bulkMatch == null) setLined(null);
  }, [bulkMatch]);

  // The other side each is offered - a transfer's, or the one it copies -
  // asked for once one of those matches is picked, and lined up with them.
  const isLiningPayments =
    bulkMatch === "transfers" || bulkMatch === "duplicates";
  const counterparts = useBackendQuery(
    Queries.TRIAGE_BOOK_LIKE_COUNTERPARTS_QUERY,
    {
      variables: { payment: triage.identifier },
      skip: !isLiningPayments,
      fetchPolicy: "cache-first",
    },
  );
  // Each payment's suggestions: the one open's own, and its payee's others'.
  const suggestionsOf = new Map<string, TriageCounterpartSuggestion[]>([
    ...(counterparts.data?.triageConnection?.data?.edges ?? []).map(
      ({ node }) =>
        [node.identifier, node.suggestions ?? []] as [
          string,
          TriageCounterpartSuggestion[],
        ],
    ),
    [triage.identifier, triage.suggestions ?? []],
  ]);
  // The queue's card of each other side: drawn as the payment beside it is.
  const otherSides = [
    triage.identifier,
    ...(bookLike?.shown ?? []).map((row) => row.card.identifier),
  ].flatMap((payment) =>
    (suggestionsOf.get(payment) ?? []).flatMap((one) =>
      one.status === "PENDING" &&
      isLiningPayments &&
      one.job === BULK_MATCH_JOBS[bulkMatch]
        ? (one.ruleSuggestionTransactionOptions ?? [])
            .slice(0, 1)
            .flatMap((option) =>
              option.transaction ? [option.transaction.identifier] : [],
            )
        : [],
    ),
  );
  const { data: otherCards } = useBackendQuery(
    Queries.TRIAGE_COUNTERPART_CARDS_QUERY,
    {
      variables: { identifiers: otherSides },
      skip: otherSides.length === 0,
      fetchPolicy: "cache-first",
    },
  );
  const cardOf = new Map(
    (otherCards?.triageConnection?.data?.edges ?? []).map(({ node }) => [
      node.identifier,
      node,
    ]),
  );
  // Two that are each other's copy: the one open - or the first - is marked
  // the copy, the other kept.
  const marked = new Set<string>();
  const offered: TriagePairedItem[] | null =
    isLiningPayments && counterparts.data
      ? [
          triage.identifier,
          ...(bookLike?.shown ?? []).map((row) => row.card.identifier),
        ].flatMap((payment) => {
          // The payment open's: the one picked, where it is of the kind -
          // as it was picked before the line-up was opened.
          const ofKind = (suggestionsOf.get(payment) ?? []).filter(
            (one) =>
              one.job === BULK_MATCH_JOBS[bulkMatch] &&
              one.status === "PENDING",
          );
          const suggestion =
            (payment === triage.identifier
              ? ofKind.find(
                  (one) =>
                    one.identifier === selections.linesSuggestionIdentifier,
                )
              : undefined) ?? ofKind[0];
          const other =
            suggestion?.ruleSuggestionTransactionOptions?.[0]?.transaction;
          if (!other || marked.has(payment)) return [];
          marked.add(other.identifier);
          return [
            {
              key: `transaction:${payment}:${other.identifier}`,
              kind: "transaction" as const,
              card: cardOf.get(other.identifier) ?? null,
              identifier: other.identifier,
              documentId: null,
              reference: null,
              name: other.description ?? "A payment",
              date: other.effectiveDate ? String(other.effectiveDate) : null,
              amount: other.amount != null ? Number(other.amount) : null,
              currency: null,
              pairedWith: payment,
              offeredTo: [payment],
              business: other.entity?.identifier ?? null,
              // Drawn as the queue's card of it, which says its business.
              businessName: null,
            },
          ];
        })
      : null;
  // Each lined up booked by its own suggestion of the kind, where that offers
  // what it is lined up with; any other said, not booked.
  const matchPairs = (
    kind: "transfers" | "duplicates",
    pairs: { payment: string; item: TriagePairedItem }[],
  ) => {
    const sent = pairs.flatMap(({ payment, item }) => {
      const suggestion = (suggestionsOf.get(payment) ?? []).find(
        (one) =>
          one.job === BULK_MATCH_JOBS[kind] &&
          one.status === "PENDING" &&
          (one.ruleSuggestionTransactionOptions ?? []).some(
            (option) => option.transaction?.identifier === item.identifier,
          ),
      );
      return suggestion ? [{ payment, suggestion: suggestion.identifier }] : [];
    });
    if (sent.length < pairs.length) {
      State.getState().setSnackbarMessage(
        `${pairs.length - sent.length} lined up with one their suggestions do not offer: left as they are.`,
      );
    }
    matchEach(kind, sent);
  };

  // What Book does, said on it and done by it, as the row picked says.
  const movePicked = moves.find((one) => one.key === picked) ?? null;
  const book = (() => {
    if (movePicked) {
      return {
        label:
          ticked.length > 0 ? `Move ${ticked.length + 1} payments` : "Move",
        // At work, it says so - not drawn as one not to be pressed.
        isDisabled: movePicked.isDone,
        isMaking: movingPaymentTo != null,
        run: () =>
          movePayment(
            movePicked.business,
            movePicked.treatment,
            ticked.map((row) => row.card.identifier),
          ),
      };
    }
    switch (picked) {
      case "transfers":
        return {
          label:
            lined?.count === 1
              ? "Match 1 transfer"
              : `Match ${lined?.count ?? 0} transfers`,
          isDisabled: !lined || lined.count === 0,
          isMaking: making === "transfers",
          run: () => lined?.match(),
        };
      case "duplicates":
        return {
          label:
            lined?.count === 1
              ? "Mark 1 duplicate"
              : `Mark ${lined?.count ?? 0} duplicates`,
          isDisabled: !lined || lined.count === 0,
          isMaking: making === "duplicates",
          run: () => lined?.match(),
        };
      case "documents":
        return {
          label: `Match ${lined?.count ?? 0} as lined up`,
          isDisabled: !lined || lined.count === 0,
          isMaking: lined?.isMatching ?? false,
          run: () => lined?.match(),
        };
      case null:
        return {
          label: "Pick how they are booked",
          isDisabled: true,
          isMaking: false,
          run: () => {},
        };
      default:
        return {
          label:
            toBook.length > 0 ? `Book ${toBook.length + 1} payments` : "Book",
          isDisabled: !(bookLikeOffer?.canBookItself ?? false),
          isMaking: making === "post",
          run: bookAll,
        };
    }
  })();
  const runRef = useRef(book.run);
  runRef.current = book.run;
  const run = useCallback(() => runRef.current(), []);
  useEffect(() => {
    setBulkBook?.({
      label: book.label,
      isDisabled: book.isDisabled,
      isMaking: book.isMaking,
      run,
    });
  }, [book.label, book.isDisabled, book.isMaking, run, setBulkBook]);
  useEffect(() => () => setBulkBook?.(null), [setBulkBook]);

  // A match picked: the panel its side of each payment - the documents they
  // might pay, each transfer's other side, the payment each copies - lined up
  // with the payments in the queue's place; its x, or Escape, the rows again.
  if (bulkMatch) {
    // Read, the payments and what is offered them; till then each line beside
    // the list's rows is a placeholder of what it would hold.
    const isReady =
      bookLike != null &&
      similarListElement != null &&
      (bulkMatch === "documents" || offered != null) &&
      Boolean(triage.identifier);
    const lined = [
      ...(bookLike?.pinned ? [bookLike.pinned] : []),
      ...(bookLike?.shown ?? []),
    ];
    return (
      <div
        data-testid="triage-bulk"
        data-match={bulkMatch}
        className="flex-1 min-h-0 flex flex-col bg-muted dark:bg-[#151719]"
      >
        <TriageDocumentPairs
          key={bulkMatch}
          kind={bulkMatch}
          isLoading={!isReady}
          paymentId={triage.id}
          rows={bookLike?.shown ?? []}
          pinned={bookLike?.pinned}
          ticked={new Set(bookLike?.ticked.map((row) => row.card.identifier))}
          keyed={
            lined.find((row) => row.card.id === bookLike?.keyed)?.card
              .identifier ?? null
          }
          // The list's keys put on a payment from beside it.
          onKeyed={(identifier) => {
            const row = lined.find((one) => one.card.identifier === identifier);
            if (row) bookLike?.setKeyed(row.card.id);
          }}
          list={similarListElement}
          size="md"
          onBack={() => {
            setBulkMatch?.(null);
            requestAnimationFrame(() =>
              document
                .querySelector<HTMLElement>(
                  "[data-testid='triage-bulk'] [data-testid='triage-suggestions-options']",
                )
                ?.focus(),
            );
          }}
          onMatched={(identifiers) => {
            if (identifiers.includes(triage.identifier)) {
              onResolved({
                alsoRemoveTransactionIdentifiers: identifiers,
              });
              return;
            }
            removeFromQueue(identifiers);
            void bookLike?.refetch();
          }}
          onLined={onLined}
          {...(offered
            ? {
                offered,
                onMatch: (
                  pairs: { payment: string; item: TriagePairedItem }[],
                ) => matchPairs(bulkMatch as "transfers" | "duplicates", pairs),
              }
            : {})}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="triage-bulk"
      className="flex-1 min-h-0 flex flex-col bg-muted dark:bg-[#151719]"
    >
      <div className="flex-1 min-h-0 flex flex-col">
        <SelectableCardList
          items={rows}
          loading={bookLike == null || bookLike.isLoading}
          loadingItemCount={4}
          placeholderItem={BULK_PLACEHOLDER}
          getItemKey={(row) => row.key}
          // A move card, the keys on it while its way picked is.
          isItemActive={(row) =>
            row.key === keyedRow ||
            (row.moves?.some((move) => move.key === keyedRow) ?? false)
          }
          onItemClick={(row) => {
            setKeyed(row.key);
            pick(row);
          }}
          onItemNavigate={(row, _index, stop) => setKeyed(stop ?? row.key)}
          onItemEnter={(row, _index, event) => {
            event.preventDefault();
            pick(row, keyedRow);
          }}
          scrollToActiveKey={keyedRow}
          // The one picked - or the keys', on another - stays in sight as
          // the list scrolls, as a picked suggestion does.
          stickyActiveRow
          keyboardScope="global"
          isKeyboardActive={isSuggestionsKeyboardActive}
          disableVirtualization
          variableItemHeight
          estimatedItemHeight={CARD_HEIGHT}
          size="md"
          variant="outlined"
          layout="cards"
          rowHover="title"
          data-testid="triage-suggestions-options"
          className="box-border"
          // Set as the payee's others beside it are: what it is over its
          // title, and its count where their amounts are.
          renderItem={({ item: row, isLoading }) => (
            <div
              data-testid={`triage-bulk-row-${row.kind}`}
              data-picked={
                row.moves
                  ? row.moves.some((move) => move.key === picked)
                  : row.key === picked
              }
            >
              {/* Drawn as a payment's card is - the one card of triage's:
                  what it is over its title, its count where an amount is. A
                  post or a move has a ball on its left edge - picked, the
                  end of a line from each payment ticked - and a post its
                  category chip, C to change it. */}
              <TriageCard
                isLoading={isLoading}
                marker={
                  row.count == null && !isLoading ? (
                    <TriageLinkBall
                      link={TRIAGE_BULK_PICK_LINK}
                      end="to"
                      isEngaged={
                        row.moves
                          ? row.moves.some((move) => move.key === picked)
                          : row.key === picked
                      }
                      onPress={() => {
                        setKeyed(row.key);
                        pick(row);
                      }}
                      label={`${row.lead} ${row.title}`}
                      testId="triage-bulk-ball"
                    />
                  ) : undefined
                }
                tools={
                  isLoading ? undefined : row.moves ? (
                    // How the two settle them, on the one chip for it: the
                    // way it is moved unless another is chosen there - which
                    // picks it.
                    <TriageSettlementChip
                      suggestion={null}
                      ways={row.moves}
                      business={row.title}
                      isOut={isOut}
                      chosen={findMoveOf(row)?.treatment ?? null}
                      moved={null}
                      testId="triage-bulk-move-settlement"
                      // M changes it on the card the keys are on.
                      hotkey={
                        row.key === keyedRow ||
                        row.moves.some((move) => move.key === keyedRow)
                          ? TRIAGE_KEYS.MOVE
                          : undefined
                      }
                      onChoose={(treatment) => {
                        setKeyed(row.key);
                        pick(
                          row,
                          row.moves?.find((one) => one.treatment === treatment)
                            ?.key,
                        );
                      }}
                    />
                  ) : row.suggestionIdentifier ? (
                    posts
                      .filter(
                        (suggestion) =>
                          suggestion.identifier === row.suggestionIdentifier,
                      )
                      .map((suggestion) => (
                        <TriageSuggestionCategoryChip
                          key={suggestion.identifier}
                          suggestion={suggestion}
                          isActive={row.key === keyedRow}
                          onPick={() => pick(row)}
                        />
                      ))
                  ) : row.kind === "post" ? (
                    // Found by the words searched for: what it files them
                    // under, as the posting card's finds say it - one chosen
                    // or named there picks it, and is what it is booked with.
                    <TriageProposalCategoryChip
                      account={
                        row.chartedAccountIdentifier
                          ? {
                              identifier: row.chartedAccountIdentifier,
                              name: row.title,
                            }
                          : null
                      }
                      suggested={row.category ?? null}
                      proposed={
                        row.category ? null : (row.categoryName ?? null)
                      }
                      chosenIdentifier={
                        row.key === picked
                          ? selections.categoryIdentifier
                          : null
                      }
                      named={
                        row.key === picked ? selections.newCategoryName : null
                      }
                      onChoose={(identifier) => {
                        setKeyed(row.key);
                        pick(row, null, {
                          categoryIdentifier: identifier,
                          newCategoryName: null,
                        });
                      }}
                      onName={(name) => {
                        setKeyed(row.key);
                        pick(row, null, {
                          categoryIdentifier: null,
                          newCategoryName: name,
                        });
                      }}
                      triage={triage}
                      isActive={row.key === keyedRow}
                    />
                  ) : undefined
                }
                crumbs={[{ label: row.note }]}
                title={
                  <>
                    <span
                      data-testid="triage-bulk-lead"
                      className="text-muted-foreground"
                    >
                      {row.lead}
                    </span>{" "}
                    {row.title}
                  </>
                }
                amount={row.count ?? undefined}
                testIds={{
                  crumbs: "triage-bulk-overline",
                  amount:
                    row.count != null ? "triage-bulk-count" : "triage-bulk-end",
                }}
              />
            </div>
          )}
        />
      </div>
    </div>
  );
}
